import { _js, ___p } from '@ifixit/localize';
import { color, space, shadow, fontSize, borderRadius, breakpoint } from '@ifixit/primitives';
import { Button, Tag, TagLabel } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import styled from 'styled-components';
import PdfIcon from 'Guide/Icons/filetypes/32/Pdf';
import { DocumentMeta } from './document_meta_types';
import Accordion from './accordion';

type DocumentMetaCardProps = {
   doc: DocumentMeta;
   children?: any;
   className?: string;
};

type DocumentMetaAccordionProps = {
   doc: DocumentMeta;
};

const formatDate = (date: any) =>
   new Date(date).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'medium',
   });
const MOBILE = `@media only screen and (max-width: ${breakpoint.sm})`;

const Container = styled.div`
   background: ${color.white};
   border-radius: ${borderRadius.md};
   box-shadow: ${shadow[1]};
`;

const Content = styled.div`
   display: flex;
   flex-flow: row wrap;
   padding: ${space[5]};
   border-bottom: 1px solid ${color.gray[200]};
   color: ${color.gray[500]};
   font-size: ${fontSize.md};

   p {
      margin: 0;
      line-height: 16px;
   }
`;

const ThumbnailSection = styled.a`
   position: relative;
   margin: 0 ${space[5]} ${space[5]} 0;

   img {
      width: ${space[11]};
      height: ${space[11]};
      box-shadow: ${shadow[1]};
      border-radius: ${borderRadius.md};
      object-fit: cover;
   }

   svg {
      position: absolute;
      top: 69px;
      left: -${space[2]};
   }
`;

const DataSection = styled.div`
   display: flex;
   flex-flow: row wrap;
   flex-grow: 1;
`;

const InfoSection = styled.div`
   display: flex;
   flex-direction: column;
   flex-grow: 1;
   margin-right: ${space[4]};
`;

const MetadataSection = styled.div`
   display: flex;
   flex-flow: column wrap;

   > p {
      margin-bottom: ${space[2]};
   }
`;

const Filename = styled.div`
   display: flex;
   flex-flow: row wrap;
   align-items: center;

   .filename {
      font-size: ${fontSize.xl};
      line-height: 22px;
      font-weight: 700;
      color: ${color.gray[800]};
   }

   > * {
      margin-bottom: ${space[2]};

      &:not(:last-child) {
         margin-right: ${space[2]};
      }
   }
`;

const DownloadSection = styled.div`
   display: flex;
   flex-flow: column wrap;
   justify-content: flex-end;
   text-align: right;

   p {
      flex-grow: 1;
      margin-bottom: ${space[2]};

      ${MOBILE} {
         text-align: left;
      }
   }

   svg {
      color: ${color.gray[500]};
   }
`;

const Thumbnail = ({
   url,
   thumbnailUrl,
   title,
}: Pick<DocumentMeta, 'url' | 'thumbnailUrl' | 'title'>) =>
   thumbnailUrl ? (
      <ThumbnailSection href={url}>
         <img src={thumbnailUrl} alt={title} />
         <PdfIcon />
      </ThumbnailSection>
   ) : null;

const Metadata = ({
   docid,
   guid,
   uploader,
   size,
   pages,
}: Pick<DocumentMeta, 'docid' | 'guid' | 'uploader' | 'size' | 'pages'>) => (
   <MetadataSection>
      <p>{'Doc ID: ' + docid}</p>
      <p>{'GUID: ' + guid}</p>
      {uploader && (
         <p>
            {_js('Uploaded by ')}
            <a href={uploader.profileUrl}>{uploader.username}</a>
         </p>
      )}
      <p>
         {size} — {___p(pages, '%1 page', '%1 pages', String(pages))}
      </p>
   </MetadataSection>
);

const Download = ({ url }: Pick<DocumentMeta, 'url'>) => (
   <Button
      rightIcon={<FontAwesomeIcon icon={faDownload} />}
      variant="link"
      as="a"
      href={url}
      color={color.brand[500]}
      justifyContent="flex-end"
   >
      {_js('Download')}
   </Button>
);

const DocumentMetaCard = ({ doc, children, className }: DocumentMetaCardProps) => (
   <Container className={className}>
      <Content>
         <Thumbnail url={doc.url} thumbnailUrl={doc.thumbnailUrl} title={doc.title} />
         <DataSection>
            <InfoSection>
               <Filename>
                  <span className="filename">{doc.filename}</span>
                  <Tag
                     background={color.green[100]}
                     padding={space[1]}
                     borderRadius={borderRadius.md}
                  >
                     <TagLabel fontWeight="bold" fontSize={fontSize.sm} color={color.green[600]}>
                        {_js('Current Version')}
                     </TagLabel>
                  </Tag>
               </Filename>
               <Metadata
                  docid={doc.docid}
                  guid={doc.guid}
                  uploader={doc.uploader}
                  size={doc.size}
                  pages={doc.pages}
               />
            </InfoSection>
            <DownloadSection>
               <p>{formatDate(doc.date)}</p>
               <Download url={doc.url} />
            </DownloadSection>
         </DataSection>
      </Content>
      {children}
   </Container>
);

const DocumentMetaAccordion = ({ doc }: DocumentMetaAccordionProps) => (
   <Accordion title={doc.filename} subtitle={formatDate(doc.date)}>
      <Content>
         <Thumbnail url={doc.url} thumbnailUrl={doc.thumbnailUrl} title={doc.title} />
         <DataSection>
            <InfoSection>
               <Metadata
                  docid={doc.docid}
                  guid={doc.guid}
                  uploader={doc.uploader}
                  size={doc.size}
                  pages={doc.pages}
               />
            </InfoSection>
            <DownloadSection>
               <Download url={doc.url} />
            </DownloadSection>
         </DataSection>
      </Content>
   </Accordion>
);

export { DocumentMetaCard, DocumentMetaAccordion };
